import React, { useEffect, useRef, useCallback } from "react";

export default function CameraModal({
  globalId,
  facingMode,
  setFacingMode,
  onClose,
  onSnap,
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const isAliveRef = useRef(true);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((t) => t.stop());
      } catch {}
      streamRef.current = null;
    }
    if (videoRef.current) {
      try {
        // @ts-ignore safari types
        videoRef.current.srcObject = null;
      } catch {}
    }
  }, []);

  const startCamera = useCallback(async () => {
    // Defensive: stop any leftover stream before starting a new one
    stopStream();

    try {
      if (!navigator?.mediaDevices?.getUserMedia) {
        console.error("Camera error: mediaDevices.getUserMedia not available");
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: false,
      });
      if (!isAliveRef.current) {
        // Component unmounted or effect cleaned up during await: tidy up
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      streamRef.current = stream;

      const video = videoRef.current;
      if (video) {
        // @ts-ignore safari types
        video.srcObject = stream;
        // @ts-ignore safari attribute
        video.playsInline = true;
        await video.play().catch(() => {
          /* ignore play promise rejection (autoplay policies) */
        });
      }
    } catch (err) {
      console.error("Camera error:", err);
    }
  }, [facingMode, stopStream]);

  useEffect(() => {
    isAliveRef.current = true;
    startCamera();
    return () => {
      isAliveRef.current = false;
      stopStream();
    };
  }, [startCamera, stopStream]);

  const takePhoto = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    onSnap?.(dataUrl);
  }, [onSnap]);

  const flipCamera = useCallback(() => {
    setFacingMode((f) => (f === "user" ? "environment" : "user"));
  }, [setFacingMode]);

  return (
    <div className="camera-modal fixed inset-0 flex items-center justify-center bg-black/50 z-50">
      <div className="bg-white rounded-lg shadow-xl p-4 w-full max-w-md">
        <div className="aspect-video bg-black rounded overflow-hidden mb-3">
          <video ref={videoRef} className="w-full h-full object-contain" />
        </div>
        <div className="flex items-center justify-between gap-2">
          <button
            className="px-3 py-2 border rounded"
            onClick={flipCamera}
            type="button"
          >
            🔄 Flip
          </button>
          <button
            className="px-3 py-2 border rounded"
            onClick={takePhoto}
            type="button"
          >
            📸 Snap
          </button>
          <button
            className="px-3 py-2 border rounded"
            onClick={onClose}
            type="button"
          >
            ❌ Cancel
          </button>
        </div>
        <div className="text-xs text-gray-500 mt-2">
          Room: <code>{globalId}</code> · Camera: <code>{facingMode}</code>
        </div>
      </div>
    </div>
  );
}
